import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataProvider';
import { buildSupportMailto } from '../utils/supportMailto';
import { activateOnEnterOrSpace } from '../utils/a11y';
import { useMediaQuery } from '../hooks/useMediaQuery';
import MobileDrawer from './MobileDrawer';

export default function AnnotatorSidebar() {
  const { user, effectiveRole, orgId, signOut } = useAuth();
  const { currentUser } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  const showConsole = ['admin', 'spl', 'god'].includes(effectiveRole);
  const referralCode = currentUser?.referral_code;
  // Phase 14.2 — collapse to hamburger drawer at md breakpoint
  const isMobile = useMediaQuery('(max-width: 768px)');

  const mainNav = [
    { to: '/', icon: '◈', label: 'Dashboard', end: true },
    { to: '/find-work', icon: '▤', label: 'Find Work' },
    { to: '/my-work', icon: '◫', label: 'My Work' },
    { to: '/assessments', icon: '◎', label: 'Assessments' },
    { to: '/earnings', icon: '▥', label: 'Earnings' },
  ];

  // Sidebar contents — same render tree at every viewport. Wrapping
  // chrome differs (inline rail at desktop, MobileDrawer panel at <= md).
  const body = (
    <>
      {/* Header — matches production: logo + org name + borderBottom */}
      <div className="sidebar-header">
        <div className="sidebar-logo">JUUNI</div>
        <div className="sidebar-subtitle"
          role="button"
          tabIndex={0}
          onClick={() => navigate('/profile')}
          onKeyDown={activateOnEnterOrSpace(() => navigate('/profile'))}
          style={{ cursor: 'pointer' }}
          title="View your profile">
          {currentUser?.name || user?.email?.split('@')[0] || 'Annotator'}
        </div>
      </div>

      {/* Nav — matches production: marginTop 8px, gap 4px, padding 0 6px */}
      <nav className="sidebar-nav">
        {showConsole && (
          <NavLink
            to={effectiveRole === 'god' ? '/god' : '/admin'}
            className="nav-item"
            style={{ background: 'rgba(93,202,165,0.06)', marginBottom: 4 }}
          >
            <span className="nav-icon">←</span>
            <span style={{ color: 'var(--text-success)', fontWeight: 500 }}>Console</span>
          </NavLink>
        )}

        {mainNav.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span className="nav-icon">{item.icon}</span>
            {item.label}
          </NavLink>
        ))}

      </nav>

      {/* Contact support with prefilled session context */}
      <a href={buildSupportMailto({ orgId, role: effectiveRole, path: location.pathname })}
         className="nav-item"
         style={{ margin: '0 6px', textDecoration: 'none' }}
         title="Email support — includes your session context">
        <span className="nav-icon">?</span>
        Contact Support
      </a>

      {/* Settings pinned above bottom */}
      <NavLink to="/settings" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        style={{ margin: '0 6px' }}>
        <span className="nav-icon">⚙</span>
        Settings
      </NavLink>

      {/* Referral card */}
      <div style={{
        padding: '8px 10px', margin: '8px 6px 0', borderRadius: 6,
        background: 'rgba(93,202,165,0.06)', border: '1px solid rgba(93,202,165,0.12)',
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
          Earn $50 per referral
        </div>
        {referralCode ? (
          <div style={{
            fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-success)',
            padding: '3px 6px', background: 'var(--bg-card)', borderRadius: 4, textAlign: 'center',
            cursor: 'pointer', border: '1px solid rgba(93,202,165,0.2)',
          }}
            role="button"
            tabIndex={0}
            onClick={() => { navigator.clipboard.writeText(referralCode); }}
            onKeyDown={activateOnEnterOrSpace(() => { navigator.clipboard.writeText(referralCode); })}
            title="Click to copy referral code"
          >
            {referralCode}
          </div>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            <span
              role="button"
              tabIndex={0}
              onClick={() => navigate('/earnings')}
              onKeyDown={activateOnEnterOrSpace(() => navigate('/earnings'))}
              style={{ color: 'var(--text-success)', cursor: 'pointer' }}>Learn more →</span>
          </div>
        )}
      </div>

      {/* Bottom — person icon + name + sign out */}
      <div className="sidebar-bottom">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg onClick={() => navigate('/profile')} style={{ cursor: 'pointer', flexShrink: 0 }} title="Edit profile" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8" cy="5" r="2.5" stroke="var(--text-muted)" strokeWidth="1.3" />
            <path d="M3 14c0-2.8 2.2-5 5-5s5 2.2 5 5" stroke="var(--text-muted)" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
          <span className="sidebar-user-name"
            role="button"
            tabIndex={0}
            onClick={() => navigate('/profile')}
            onKeyDown={activateOnEnterOrSpace(() => navigate('/profile'))}
            style={{ cursor: 'pointer', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', transition: 'color 0.15s' }} title="Edit profile"
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-success)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = ''; }}>
            {currentUser?.name || user?.email?.split('@')[0]}
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={signOut}
            onKeyDown={activateOnEnterOrSpace(signOut)}
            style={{
            fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', fontFamily: 'var(--font-sans)', flexShrink: 0,
          }}>Sign out</span>
        </div>
      </div>
    </>
  );

  // Phase 14.2 — at md breakpoint and below, hamburger + MobileDrawer.
  // Above md, render the inline rail as before.
  if (isMobile) {
    const hamburger = (
      <button
        type="button"
        className="hamburger-button"
        aria-label="Open navigation menu"
        aria-haspopup="dialog"
      >
        ☰
      </button>
    );
    return (
      <MobileDrawer trigger={hamburger} ariaLabel="Annotator navigation">
        <div className="annotator-sidebar">{body}</div>
      </MobileDrawer>
    );
  }

  return <div className="sidebar annotator-sidebar">{body}</div>;
}
